// src/pages/chat/context/chat.tsx
import React, { useEffect, useState, useRef } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { Inbox } from "common/types/common.type";
import { useAuth } from "common/context/auth";
import { fetchChats, fetchBootstrap, markChatRead } from "api/botlinkd";

type User = {
  name: string;
  image: string;
};

type ChatContextProp = {
  user: User;
  inbox: Inbox[];
  activeChat?: Inbox;
  onChangeChat: (chat: Inbox) => void;
  isLoading: boolean;
};

const initialValue: ChatContextProp = {
  user: {
    name: "BotLinkd Cloud",
    image: "/assets/images/1753191706tY5Arcb3AqKQt1Dte3cW.jpg",
  },
  inbox: [],
  onChangeChat() {
    throw new Error("ChatContext not initialized");
  },
  isLoading: false,
};

export const ChatContext = React.createContext<ChatContextProp>(initialValue);

export default function ChatProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const { appkey, authkey } = useAuth();

  const [user] = useState<User>(initialValue.user);
  const [inbox, setInbox] = useState<Inbox[]>([]);
  const [activeChat, setActiveChat] = useState<Inbox | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  const unreadCountsRef = useRef<Record<string, number>>({});
  const activeChatIdRef = useRef<string | null>(null);

  // Prepare notification sound once
  useEffect(() => {
    if (!notificationAudioRef.current) {
      try {
        const audio = new Audio("/assets/sounds/incoming-message.mp3");
        audio.volume = 0.4;
        notificationAudioRef.current = audio;
      } catch (err) {
        console.warn("Failed to init chat notification sound", err);
      }
    }
  }, []);

  // Keep latest active chat id in a ref
  useEffect(() => {
    activeChatIdRef.current = activeChat ? String(activeChat.id) : null;
  }, [activeChat]);

  // Main Effect: Load Chats + Setup Pusher Realtime
  useEffect(() => {
    if (!appkey || !authkey) {
      setInbox([]);
      setActiveChat(undefined);
      unreadCountsRef.current = {};
      activeChatIdRef.current = null;
      return;
    }

    // FIX: Use 'any' to avoid TS2314 error regarding generic arguments
    let echo: any = null; 
    let intervalId: number | undefined;
    let isCancelled = false;

    // --- The Core Loader Function ---
    const loadChats = async (isBackgroundRefresh = false) => {
      try {
        if (!isBackgroundRefresh) setIsLoading(true);
        
        const chats = await fetchChats(appkey, authkey);

        if (isCancelled) return;

        const mapped: Inbox[] = (chats || []).map((chat: any): Inbox => {
          const name = chat.phone || chat.name || "Unknown";
          const ts = chat.last_time ? new Date(chat.last_time) : null;
          const timestamp = ts
            ? ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            : undefined;

          const unreadCount = typeof chat.unread_count === "number" ? chat.unread_count : 0;

          let lastMessage = chat.last_message || undefined;
          // Handle "call" body artifact
          if (lastMessage && lastMessage.toLowerCase() === "call") {
            lastMessage = "[Media]";
          }

          return {
            id: String(chat.id),
            name,
            image: "/assets/images/1753190962uKqU69GWzWSkWDv9P9vz.png",
            lastMessage,
            timestamp,
            messageStatus: unreadCount > 0 ? "SENT" : "READ",
            notificationsCount: unreadCount || undefined,
            isPinned: false,
            isOnline: false,
            autoReplyEnabled: !!chat.auto_reply_enabled,
          };
        });

        setInbox(mapped);

        // --- Sound Notification Logic ---
        try {
          const prevUnread = unreadCountsRef.current || {};
          const nextUnread: Record<string, number> = {};
          const activeId = activeChatIdRef.current;
          const hasPrevUnread = Object.keys(prevUnread).length > 0;

          mapped.forEach((item) => {
            const current = item.notificationsCount || 0;
            nextUnread[item.id] = current;
            const prev = prevUnread[item.id] ?? 0;

            // Play sound if NEW unread messages arrive for a chat that isn't active
            if (
              hasPrevUnread &&
              current > prev &&
              (!activeId || activeId !== String(item.id))
            ) {
              if (notificationAudioRef.current) {
                notificationAudioRef.current.currentTime = 0;
                void notificationAudioRef.current.play();
              }
            }
          });
          unreadCountsRef.current = nextUnread;
        } catch (err) {
          console.warn("Sound logic error", err);
        }

        // --- Maintain Active Chat ---
        if (mapped.length > 0) {
          setActiveChat((prev) => {
            if (prev) {
              const still = mapped.find((c) => c.id === prev.id);
              if (still) return still;
            }
            return mapped[0];
          });
        } else {
          setActiveChat(undefined);
        }

      } catch (err) {
        console.error("Failed to load chats", err);
      } finally {
        if (!isCancelled && !isBackgroundRefresh) {
          setIsLoading(false);
        }
      }
    };

    // 1. Initial Load
    loadChats();

    // 2. Setup Pusher (Realtime)
    const setupRealtime = async () => {
      try {
        const boot = await fetchBootstrap(appkey, authkey);
        
        if (boot.pusher && boot.pusher.key) {
          // Expose Pusher to window for Laravel Echo
          (window as any).Pusher = Pusher;

          echo = new Echo({
            broadcaster: "pusher",
            key: boot.pusher.key,
            cluster: boot.pusher.cluster ?? undefined, 
            forceTLS: true,
            // Custom Authorizer for Private Channels
            authorizer: (channel: any, options: any) => {
                return {
                    authorize: (socketId: string, callback: Function) => {
                        fetch("https://botlinkd.com/api/whatsapp/pusher/auth", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "Accept": "application/json"
                            },
                            body: JSON.stringify({
                                socket_id: socketId,
                                channel_name: channel.name,
                                appkey,
                                authkey
                            })
                        })
                        .then(response => response.json())
                        .then(data => {
                            if (data.auth) {
                                callback(false, data);
                            } else {
                                callback(true, "Auth failed");
                            }
                        })
                        .catch(error => {
                            callback(true, error);
                        });
                    }
                };
            }
          });

          // Subscribe to channel
          echo.channel(boot.pusher.channel);
          
          // Use underlying Pusher instance to bind global events
          if (echo.connector && echo.connector.pusher) {
            echo.connector.pusher.bind_global((eventName: string, data: any) => {
               if (eventName.indexOf('pusher:') === 0) return;
               
               console.log("Realtime Event:", eventName, data);
               loadChats(true);
            });
          }
        }
      } catch (err) {
        console.error("Pusher setup failed", err);
      }
    };
    setupRealtime();

    // 3. Fallback Poll (Reduced frequency to 15s to save server load)
    intervalId = window.setInterval(() => {
      loadChats(true);
    }, 15000);

    return () => {
      isCancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      if (echo) echo.disconnect();
    };
  }, [appkey, authkey]);

  const handleChangeChat = (chat: Inbox) => {
    setActiveChat(chat);

    // Optimistically clear badge
    setInbox((prev) =>
      prev.map((item) =>
        item.id === chat.id
          ? { ...item, notificationsCount: 0, messageStatus: "READ" }
          : item
      )
    );

    if (appkey && authkey) {
      markChatRead(appkey, authkey, chat.id).catch((err) => {
        console.error("Failed to mark chat as read", err);
      });
    }
  };

  return (
    <ChatContext.Provider
      value={{
        user,
        inbox,
        activeChat,
        onChangeChat: handleChangeChat,
        isLoading,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChatContext = () => React.useContext(ChatContext);